import type { A2uiSurfaceAdapterRovoMessage } from '@atlassian/a2ui-renderer-next/v0_9/a2ui-surface-adapter-rovo';
import {
	isStoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { getStoreMessageId } from './getStoreMessageId';
import { findAddressableSurfaces } from './findAddressableSurfaces';
import { isCreateSurface } from './isCreateSurface';
import { surfaceIdOf } from './surfaceIdOf';

/**
 * Routes live A2UI messages onto the store's messages by surfaceId. Untouched messages keep
 * identity, and per-message `agenticUiMessages` arrays are append-only (the renderer slices the
 * tail incrementally). An update to an addressable surface appends to that surface's anchor; a
 * createSurface for a new surface opens on `createFallbackMessageId` and becomes addressable
 * there; a re-emitted createSurface, or an update to a surface no longer live, is dropped.
 *
 * `addressable` is refreshed from `messages` when an anchor id it holds no longer exists (the
 * final response swaps the turn message's tmp id for the real one).
 */
export const routeA2UIMessages = ({
	messages,
	incomingA2UIMessages,
	createFallbackMessageId,
	addressable = findAddressableSurfaces(messages),
}: {
	messages: StoreChatItem[];
	incomingA2UIMessages: A2uiSurfaceAdapterRovoMessage[];
	/** Target for createSurface messages of unseen surfaces (the current turn's message). */
	createFallbackMessageId: string;
	/** This turn's surfaceId -> anchor messageId map; mutated as surfaces are created. */
	addressable?: Map<string, string>;
}): StoreChatItem[] => {
	const anchorIdExists = (anchorId: string) =>
		messages.some(
			(message) => isStoreAssistant(message) && getStoreMessageId(message) === anchorId,
		);
	if (![...addressable.values()].every(anchorIdExists)) {
		const fresh = findAddressableSurfaces(messages);
		addressable.clear();
		fresh.forEach((anchorId, surfaceId) => addressable.set(surfaceId, anchorId));
	}

	const routedByAnchorId = new Map<string, A2uiSurfaceAdapterRovoMessage[]>();
	for (const a2uiMessage of incomingA2UIMessages) {
		const surfaceId = surfaceIdOf(a2uiMessage);
		const isCreate = isCreateSurface(a2uiMessage);
		let targetId = surfaceId !== undefined ? addressable.get(surfaceId) : undefined;
		if (targetId !== undefined && isCreate) {
			continue;
		}
		if (targetId === undefined) {
			if (!isCreate) {
				continue;
			}
			targetId = createFallbackMessageId;
			if (surfaceId) {
				addressable.set(surfaceId, createFallbackMessageId);
			}
		}
		const pending = routedByAnchorId.get(targetId);
		if (pending) {
			pending.push(a2uiMessage);
		} else {
			routedByAnchorId.set(targetId, [a2uiMessage]);
		}
	}

	if (routedByAnchorId.size === 0) {
		return messages;
	}

	return messages.map((message) => {
		if (!isStoreAssistant(message)) {
			return message;
		}
		const a2uiMessages = routedByAnchorId.get(getStoreMessageId(message));
		if (!a2uiMessages || a2uiMessages.length === 0) {
			return message;
		}
		return {
			...message,
			agenticUiMessages: [...(message.agenticUiMessages ?? []), ...a2uiMessages],
		};
	});
};
